/**
 * The shared visual language for signed containers (.asice/.bdoc/.ddoc):
 * a seal on the file icon, the "in .asice" chip, and — in the signature panel
 * only — a written status chip.
 *
 * Folders, List and Timeline all render the same seal so a signed document
 * looks identical wherever it appears; none of them writes the status out.
 */

import { ShieldAlert, ShieldCheck, ShieldQuestion, Signature } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { getFileExtension } from "../../constants/fileTypes";
import type { SignatureStatus } from "../../signing/signatureModel";

/** Colour + glyph per status — one table so every surface agrees.
 *  `signed` takes the documents page's Verified green (VerifiedBadge), the
 *  app's one "done" colour. Its glyph stays the signature, never a checkmark:
 *  `signed` means "signatures recorded", NOT "verified valid" (validity is
 *  `unknown` until a trust-list verifier exists). */
const STATUS_STYLE: Record<
  SignatureStatus,
  { chip: string; seal: string; icon: LucideIcon; sealIcon: LucideIcon }
> = {
  signed: {
    chip: "border-success/40 bg-success/10 text-success",
    seal: "text-success",
    icon: Signature,
    sealIcon: Signature,
  },
  issue: {
    chip: "border-amber-200 bg-amber-50 text-amber-700",
    seal: "text-amber-600",
    icon: ShieldAlert,
    sealIcon: ShieldAlert,
  },
  unsigned: {
    chip: "border-slate-200 bg-slate-100 text-slate-600",
    seal: "text-slate-500",
    icon: ShieldQuestion,
    sealIcon: ShieldQuestion,
  },
  unverified: {
    chip: "border-slate-200 bg-slate-100 text-slate-600",
    seal: "text-slate-500",
    icon: ShieldQuestion,
    sealIcon: ShieldQuestion,
  },
};

interface SignedFileIconProps {
  filename: string;
  status: SignatureStatus;
  /** Classes for the wrapper span (positioning — the seal hangs off it). */
  className?: string;
  /** Size override for the inner file-type icon, so a row whose unsigned
   *  siblings shrink theirs (e.g. the timeline's h-5) can match them. */
  iconClassName?: string;
}

/**
 * The regular file-type icon with a signature seal on its corner. Used for the
 * container row itself and for documents enclosed in one.
 */
export function SignedFileIcon({
  filename,
  status,
  className,
  iconClassName,
}: SignedFileIconProps) {
  const SealIcon = STATUS_STYLE[status].sealIcon;
  return (
    <span className={cn("relative inline-flex", className)}>
      <FileTypeIcon filename={filename} className={iconClassName} />
      <SealIcon
        className={cn(
          "absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full bg-white",
          STATUS_STYLE[status].seal
        )}
        aria-hidden="true"
      />
    </span>
  );
}

/** Label for a status, in the active language. */
function useSignatureStatusLabel(status: SignatureStatus): string {
  const { t } = useLanguage();
  const labels = t.documents.signedContainer;
  if (status === "signed") return labels.signed;
  if (status === "issue") return labels.signatureIssue;
  // An ASiC-E container IS a signed envelope; what Franklin lacks for this one
  // is the signer details, because it was signed somewhere else.
  if (status === "unsigned") return labels.panel.unsigned;
  return labels.panel.fieldSignaturesUnknown;
}

interface SignatureStatusChipProps {
  status: SignatureStatus;
  className?: string;
}

/** "Signed" / "Signed outside Franklin" / "Signature issue" pill.
 *  Panel-only — the document lists carry status as the seal alone. */
export function SignatureStatusChip({ status, className }: SignatureStatusChipProps) {
  const label = useSignatureStatusLabel(status);
  const Icon = STATUS_STYLE[status].icon;

  return (
    <span
      className={cn(
        "inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
        STATUS_STYLE[status].chip,
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

interface InContainerChipProps {
  /** Filename of the container the document is sealed in. */
  containerFilename: string;
  className?: string;
}

/** "in .asice" — names the container format a document is enclosed in. */
export function InContainerChip({ containerFilename, className }: InContainerChipProps) {
  const { t } = useLanguage();
  const ext = getFileExtension(containerFilename);
  if (!ext) return null;

  return (
    <span
      className={cn(
        "inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600",
        className
      )}
      title={containerFilename}
    >
      <ShieldCheck className="h-3 w-3" />
      {t.documents.signedContainer.inContainerChip.replace("{ext}", `.${ext}`)}
    </span>
  );
}
